import { graphql, fetchQuery, type Environment } from 'react-relay';
import { OPERAND_TYPE_VALUE } from '@atlaskit/jql-ast';
import type {
	EndCursor,
	OptionResponse,
	AvatarOption,
	SearchTemplate,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type { CloudId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { PropsResolver } from '../../../../../../common/types.tsx';
import {
	generateOptions,
	generateErrorOptions,
	fireJqlBuilderBasicErrorAnalytics,
} from '../../../../../../common/utils.tsx';
import type {
	getTeamsPropsQuery,
	JiraJqlViewContext,
} from './__generated__/getTeamsPropsQuery.graphql';

type Params = {
	fieldId: string;
	operandDisplayValues?: string[];
	jql?: string;
	environment: Environment;
	cloudId: CloudId;
	searchTemplate: SearchTemplate;
	viewContext: JiraJqlViewContext | null;
};

type GetFieldValueParams = {
	environment: Environment;
	cloudId: CloudId;
	lowerCaseFieldId: string;
	jql?: string;
	search: string;
	endCursor: EndCursor;
	viewContext: JiraJqlViewContext | null;
};

const getFieldValueOptions = async ({
	environment,
	cloudId,
	lowerCaseFieldId,
	jql,
	search,
	endCursor,
	viewContext,
}: GetFieldValueParams): Promise<OptionResponse> => {
	const after = endCursor;

	const data = await fetchQuery<getTeamsPropsQuery>(
		environment,
		graphql`
			query getTeamsPropsQuery(
				$cloudId: ID!
				$first: Int = 10
				$jqlTerm: String!
				$jql: String!
				$searchString: String!
				$after: String
				$viewContext: JiraJqlViewContext
			) {
				jira @required(action: THROW) {
					jqlBuilder(cloudId: $cloudId) {
						fieldValues(
							first: $first
							jqlTerm: $jqlTerm
							jqlContext: $jql
							searchString: $searchString
							after: $after
							viewContext: $viewContext
						) {
							totalCount
							pageInfo {
								endCursor
							}
							edges {
								node {
									... on JiraJqlTeamFieldValue {
										jqlTerm
										team {
											teamId
											name
											avatar {
												small
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
			first: 10,
			jqlTerm: lowerCaseFieldId,
			jql: jql || '',
			searchString: search,
			after,
			viewContext,
		},
		{ fetchPolicy: 'store-or-network' },
	).toPromise();

	if (!data?.jira?.jqlBuilder?.fieldValues?.edges) {
		return generateOptions([]);
	}

	const { edges: fieldValues, totalCount, pageInfo } = data.jira.jqlBuilder.fieldValues;
	return generateOptions(
		fieldValues.reduce<Array<AvatarOption>>((options, edge) => {
			if (!edge || !edge.node) {
				return options;
			}
			const { node } = edge;
			// Type guard to ensure we have a team field value
			if ('team' in node && node.team) {
				const { team } = node;
				options.push({
					label: team.name || '',
					value: node.jqlTerm || '',
					optionType: 'avatar',
					avatar: team.avatar?.small,
					itemType: OPERAND_TYPE_VALUE,
					secondaryLabel: `Team ID: ${team.teamId}`,
					square: true,
				});
			}
			return options;
		}, []),
		totalCount,
		pageInfo?.endCursor || undefined,
	);
};

export const getTeamsProps = ({
	fieldId,
	operandDisplayValues,
	jql,
	environment,
	cloudId,
	searchTemplate,
	viewContext,
}: Params): PropsResolver => ({
	enableFullList: true,
	operandDisplayValues,
	loadOptions: async (search, renderInitialValues, endCursor) => {
		if (!renderInitialValues && search.length === 0) {
			return generateOptions([]);
		}
		try {
			return await getFieldValueOptions({
				environment,
				cloudId,
				lowerCaseFieldId: fieldId.toLowerCase(),
				jql,
				search,
				endCursor,
				viewContext,
			});
		} catch (ex: unknown) {
			const error = ex instanceof Error ? ex : new Error('Unknown error');
			fireJqlBuilderBasicErrorAnalytics('teamsJqlBuilderBasicFieldValues', searchTemplate, error);
			return generateErrorOptions(error);
		}
	},
});
